import React from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Check } from 'lucide-react';

const PricingTier = ({ title, subtitle, price, period, features, popular, color = 'sortio-secondary', cta = null }) => (
  <div
    className={`bg-apple-700/40 backdrop-blur-md p-8 rounded-3xl border border-apple-600/30 ${
      popular ? 'ring-2 ring-' + color : ''
    } relative overflow-hidden transition-all duration-500 hover:translate-y-[-8px] flex flex-col`}
  >
    {popular && (
      <div className={`absolute -top-3 -right-3 bg-${color} text-white px-3 py-1 rounded-full text-xs font-medium`}>
        {/* POPULAR */}
      </div>
    )}

    <div className={`absolute -left-20 -bottom-20 w-40 h-40 rounded-full bg-${color}/20 opacity-30 blur-3xl`}></div>

    <h3 className="text-xl font-medium mb-2 text-apple-50">{title}</h3>
    <p className="text-apple-300 mb-4">{subtitle}</p>

    <div className="mb-6">
      <span className="text-4xl font-bold text-apple-50">{price}</span>
      <span className="text-apple-300 ml-1">{period}</span>
    </div>

    <ul className="space-y-3 mb-6 flex-grow">
      {features.map((feature, index) => (
        <li key={index} className="flex items-start">
          <Check className={`h-5 w-5 text-${color} mr-2 mt-0.5 flex-shrink-0`} />
          <span className="text-apple-300">{feature}</span>
        </li>
      ))}
    </ul>

    {cta && <div className="mt-auto">{cta}</div>}
  </div>
);

const BusinessPricing = () => (
  <section id="pricing" className="mb-16">
    <div className="text-center mb-12">
      <div className="inline-block mb-4">
        <div className="flex items-center justify-center p-1.5 rounded-full bg-sortio-primary/10 px-4 py-1.5">
          <DollarSign className="mr-2 text-sortio-primary" size={14} />
          <span className="text-xs font-medium uppercase tracking-wider text-sortio-primary">Simple Pricing</span>
        </div>
      </div>
      <h2 className="text-4xl font-semibold mb-5 tracking-tight text-apple-50">
        Business <span className="apple-gradient-text">Pricing</span>
      </h2>
      <p className="text-apple-300 text-lg mx-auto font-light max-w-2xl mb-2">
        Two plans for teams and enterprises. No minimum seat requirements — start with any number of seats.
      </p>
      <p className="text-apple-400 text-xs mx-auto max-w-2xl mb-5">
        *Prices in USD. Just need a single seat? See{' '}
        <a href="/#pricing" className="text-sortio-primary hover:underline">
          individual pricing
        </a>
        . For volume discounts, contact{' '}
        <a href="mailto:marcus@getsortio.com" className="text-sortio-primary hover:underline">
          marcus@getsortio.com
        </a>
        .
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
      <PricingTier
        title="Team"
        subtitle="Shared workflows for your team"
        price="$29"
        period="per seat / month"
        popular={true}
        color="sortio-primary"
        features={[
          'Double the Pro AI allowance per seat, pooled across your team',
          'Add seats to grow your shared allowance',
          'Shared automations & rules',
          'Admin console & seat management',
          'Centralized LLM policy',
          'Clio + integrations',
          'Knowledge Graph indexing',
          'Watch folders & on-change automations',
          'Priority support (24h response)',
        ]}
        cta={
          <Link to="/checkout/team" className="block">
            <button className="w-full py-3 rounded-xl text-base font-medium text-white bg-sortio-primary hover:bg-sortio-primary/90 transition-all shadow-lg shadow-sortio-primary/25">
              Get Team
            </button>
          </Link>
        }
      />

      <PricingTier
        title="Enterprise"
        subtitle="Advanced security & compliance"
        price="$50+"
        period="per seat / month"
        color="sortio-secondary"
        features={[
          'Everything in Team',
          'Volume licensing (25+ seats)',
          'Audit logs',
          'Audit logs & compliance reporting',
          'SSO or dedicated deployment: ask us',
          'Priority support',
          
        ]}
      />
    </div>

    <div className="mt-10 bg-apple-700/30 backdrop-blur-md p-8 rounded-3xl border border-apple-600/30 text-center relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-sortio-primary/5 to-sortio-secondary/5 opacity-30 pointer-events-none"></div>

      <h3 className="text-2xl font-medium mb-3 text-apple-50">Ready to talk?</h3>
      <p className="text-apple-300 mb-6 max-w-2xl mx-auto">
        Get a tailored demo, walk through deployment options, and lock in seat pricing for your team.
      </p>
      <a
        href="mailto:marcus@getsortio.com?subject=Sortio Business Pricing Inquiry"
        className="inline-block bg-gradient-to-r from-sortio-primary to-sortio-primary/80 text-white font-medium px-8 py-3 text-base rounded-xl shadow-lg shadow-sortio-primary/20 hover:shadow-sortio-primary/30 hover:brightness-110 transition-all duration-300"
      >
        Contact Sales
      </a>
      <p className="text-xs text-apple-400 mt-4 max-w-2xl mx-auto">
        Team and Enterprise subscriptions renew automatically until canceled. Cancellation and refund terms are described in our <a href="/refund-policy" className="text-sortio-primary hover:text-sortio-primary/80 underline">Refund Policy</a> and <a href="/terms-and-conditions" className="text-sortio-primary hover:text-sortio-primary/80 underline">Terms</a>.
      </p>
    </div>
  </section>
);

export default BusinessPricing;
